import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useMarketStatus } from "./utils/marketStatus";
// UI is imported for the page background only — the rest of the shell still
// reads T until the palette is approved for the remaining screens.
import { UI, FONT } from "./constants/tokens";
import { TR, CURRENCIES, TICKER_SECTORS } from "./constants/data";
import { fetchQuote, fetchQuotes, fetchProfile } from "./utils/finnhub";
import { useFxRates } from "./utils/fxRates";
import { classifySymbol } from "./utils/sectorClassify";
import { getCachedSector, setCachedSector } from "./utils/sectorCache";
import { loadOrCreatePortfolio, loadPositions, addPosition, removePosition, ensureProfile, seedDemoPositions, DEMO_WATCHLIST } from "./lib/portfolioService";
import { loadAlerts, addAlert, removeAlert } from "./lib/alertsService";
import { loadHoldingMarks, removeHoldingMark } from "./utils/holdingSegments";
import { trackScreen, trackEvent, EVENTS } from "./lib/appEvents";
import { saveAttribution } from "./lib/attribution";
import { openPrivacy } from "./lib/privacyRoute";
import AdminDashboard from "./components/Admin/AdminDashboard";
import { supabase } from "./lib/supabase";
import { useIsMobile, useIsDesktop } from "./utils/hooks";
import TickerTape from "./components/TickerTape";
import HomeTab from "./components/HomeTab";
import StockPage from "./components/StockPage";
import ThemeToggle from "./components/ThemeToggle";
import PortfolioTab from "./components/PortfolioTab";
import SimAccountTab from "./components/SimAccountTab";
import NewsTab from "./components/NewsTab";
import AlertsDrawer from "./components/AlertsTab";
import SettingsTab from "./components/SettingsTab";
import AIAnalysisTab from "./components/AIAnalysisTab";
import ExposureTab from "./components/ExposureTab";
import CalculatorsTab from "./components/CalculatorsTab";
import ScenarioTab from "./components/ScenarioTab";
import { ACADEMY_ENABLED, INVESTOR_PROFILE_ENABLED, CALCULATORS_ENABLED, PRICE_ALERTS_ENABLED } from "./constants/features";
import CalendarTab from "./components/CalendarTab";
import QATab from "./components/QATab";
import MobileSidebar from "./components/MobileSidebar";
import OnboardingFlow, { PROFILE_KEY, NameOnlyFlow } from "./components/OnboardingFlow";
import DisclaimerConsent from "./components/DisclaimerConsent";
import MyProfileTab from "./components/MyProfileTab";
import {
  IconHome, IconPortfolio, IconSimulator, IconNews,
  IconAI, IconSettings, IconCalendar, IconCalculator, IconAcademy, IconQA, IconProfile, IconGlobe, IconScenario,
} from "./components/NavIcons";
import { getFirstName, greetingWithName } from "./utils/userName";
import { isStaleQuote } from "./utils/quoteState";
import { loadWatchlist, addWatch, removeWatch } from "./lib/watchlistService";
import { readLocalCurrency, writeLocalCurrency, loadProfileCurrency, saveProfileCurrency } from "./utils/displayCurrency";
import { tlvTime, nyTime } from "./utils/marketHours";
import Icon from "./components/Icon";
import HorizonLine from "./components/HorizonLine";
import logo256 from "./assets/suricata-logo-256.png";
import ChunkBoundary from "./components/ChunkBoundary";
import { Skeleton } from "./components/Skeleton";

// The Academy and its content load when it opens (sprint 13, 6.2).
const AcademyTab = lazy(() => import("./components/AcademyTab"));

const MOBILE_TAB_IDS = ["home", "portfolio", "news", "calendar", "ai"];
// Screens that carry the "התיק היום" line. Home and portfolio each have a
// value card that already states the same move with its dollar amount and
// context, so the line there was the same number twice; the other three have
// no card, and the line is the one reminder of how the portfolio did today.
const DAY_MOVE_TABS  = ["news", "calendar", "ai"];
const GUEST_BANNER_H = 28; // px — thin guest-mode banner under the ticker strip

// A quote answer, applied to one holding. Finnhub answers an unknown or
// delisted symbol with zeros, not with an error — so a zero IS an answer
// ("nothing trades under this name today"), while null is no answer at all
// (the proxy failed, or the symbol was dropped from the batch). Only the first
// becomes "none"; the second leaves the holding exactly as it was. A holding
// that has already shown a live price never falls back to "none" on a later
// zero — one glitched poll must not blank a number the user has just read.
// Until the first answer the holding is "pending" and carries its buy price
// as a placeholder, as it always did.
function withQuote(s, q) {
  if (!q) return s;
  // `quoteAt` is Finnhub's own timestamp for the price (seconds), so a screen
  // can say when the number is from rather than implying "now".
  // A last trade from long ago (a delisted stock) is not a current price
  // (sprint 13, 5.5 · utils/quoteState.js): no quote, with the date of the last.
  if (q.c > 0 && isStaleQuote(q.t)) return { ...s, currentPrice: null, prevClose: null, quote: "none", lastQuoteAt: q.t };
  if (q.c > 0) return { ...s, currentPrice: q.c, prevClose: q.pc, quote: "live", quoteAt: Number.isFinite(q.t) && q.t > 0 ? q.t : null };
  if (s.quote === "live") return s;
  return { ...s, currentPrice: null, prevClose: null, quote: "none" };
}
// While the Academy's chunk loads (sprint 13, 6.2) · a quiet placeholder.
function AcademyLoading() {
  return <Skeleton h={160} radius={12} style={{ margin: "8px 0" }} label="טוען את האקדמיה"/>;
}
const STATUS_BAR_H   = 26; // px — permanent status row (desktop: fixed bottom bar)
// Mobile: the fixed bar at the foot (status row + nav) is 90px; the page
// leaves 30px more under its last card, so the bar never covers one
// (sprint 17 · it left 12px, and a card's last line sat under the bar).
const MOBILE_BOTTOM_PAD = 120;

// Permanent status row: the legal line + TLV/NY wall clocks with an
// open/closed dot (clock arithmetic only — no invented market data).
// Desktop renders it as a fixed bottom bar; mobile embeds it as a thin strip
// on top of the bottom-nav container so it never hides content.
// `marketTag`: the US market's state beside the clocks · off on the home
// screen, whose badge says it at the top (sprint 19 wording round).
function StatusRow({ standalone, lang = "he", marketTag = true }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force(x => x + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  // One clock cell: the time on that exchange's own clock, from the timezone
  // database. The open/closed dot that used to sit beside it is gone — it was
  // computed from hardcoded session hours with no source, and the Israeli
  // ones had been wrong since the exchange moved to a Mon–Fri week in
  // January (see utils/marketHours.js).
  const clock = (label, time) => {
    if (!time) return null;
    return (
      <span className="si-num" style={{ fontSize: 11, color: UI.text.muted }}>{label} {time}</span>
    );
  };
  // The US market's state, beside the New York clock (utils/marketStatus.js).
  const mkt = useMarketStatus();
  const mktTag = { pre: "לפני פתיחה", post: "אחרי סגירה", closed: "השוק סגור" }[mkt.state];
  return (
    <div style={{
      ...(standalone
        ? { position: "fixed", bottom: 0, left: 0, right: 0, height: STATUS_BAR_H, background: UI.surface.sunken, borderTop: `1px solid ${UI.line.hairline}`, zIndex: 100 }
        : { height: 22 }),
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 14px",
    }}>
      {/* The one disclaimer of the app (sprint 17): the screens no longer
          repeat it, so it speaks the screen's language. */}
      <span style={{ fontSize: 11, color: UI.text.muted }}>{lang === "en" ? "Information only · Not investment advice" : "מידע בלבד · אינו ייעוץ השקעות"}</span>
      <span style={{ display: "inline-flex", gap: 14 }}>
        {clock("TLV", tlvTime())}
        {clock("NY", nyTime())}
        {marketTag && mktTag && <span style={{ fontSize: 10.5, fontWeight: 700, color: UI.text.muted, border: `1px solid ${UI.line.hairline}`, borderRadius: 999, padding: "0 7px", lineHeight: "16px" }}>{mktTag}</span>}
      </span>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App({ onSignOut, session }) {
  // Guest mode = Supabase anonymous session. Drives demo seeding, the guest
  // banner, questionnaire skip, and account-management blocking.
  const isGuest = !!session?.user?.is_anonymous;
  const isMobile = useIsMobile();
  // Ticker strip is 36px on mobile (touch/legibility), 30px on desktop —
  // every sticky offset below the strip derives from this one value.
  const tickerH = isMobile ? 36 : 30;
  const [tab,          setTab]          = useState("home");
  // One subscription here re-renders the tree when a rate lands, so every
  // fmt() below it (a plain function, no hook) picks up the live figure.
  const market = useMarketStatus();
  const fxRates = useFxRates();
  const [isPro,        setIsPro]        = useState(true);
  const [portfolio,    setPortfolio]    = useState([]);
  const [watchlist,    setWatchlist]    = useState([]);
  const [portfolioId,  setPortfolioId]  = useState(null);
  const [alerts,    setAlerts]    = useState([]);
  const [triggered, setTriggered] = useState([]);
  const [mounted,   setMounted]   = useState(false);
  // Have the holdings come back yet? Distinguishes "still loading" from
  // "this account holds nothing", which look identical in `portfolio`.
  const [portfolioReady, setPortfolioReady] = useState(false);
  const [settings] = useState({ alertsEnabled: true, newsAlerts: true, showSparklines: true });
  // The display currency is remembered (sprint 13, 5.1 · utils/displayCurrency.js):
  // on this device always, and in the profile of a registered user.
  const [cur, setCurState] = useState(() => readLocalCurrency() || "USD");
  const setCur = (code) => {
    setCurState(code);
    writeLocalCurrency(code);
    if (session?.user?.id && !session.user.is_anonymous) saveProfileCurrency(session.user.id, code);
  };
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid || session.user.is_anonymous) return undefined;
    let alive = true;
    loadProfileCurrency(uid).then(code => {
      if (alive && code) { setCurState(code); writeLocalCurrency(code); }
    });
    return () => { alive = false; };
  }, [session?.user?.id, session?.user?.is_anonymous]); // eslint-disable-line react-hooks/exhaustive-deps
  // What the selector shows: the rate every figure is converted by, and its
  // date, or the plain fact that there is none right now.
  const fxLabel = cur === "USD"
    ? `${CURRENCIES.USD.symbol} USD`
    : fxRates[cur]
      ? `${CURRENCIES[cur]?.symbol} ${cur} · ${fxRates[cur].rate.toFixed(3)}${fxRates[cur].date ? ` · ${fxRates[cur].date}` : ""}`
      : `${CURRENCIES[cur]?.symbol} ${cur} · אין שער חי`;
  const [showAcademy,   setShowAcademy]   = useState(false);
  const [showCalc,      setShowCalc]      = useState(false);
  const [showQA,        setShowQA]        = useState(false);
  const [showProfile,   setShowProfile]   = useState(false);
  const [showSettings,  setShowSettings]  = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [showExposure,  setShowExposure]  = useState(false);
  const [showScenario,  setShowScenario]  = useState(false);
  const [showSidebar,   setShowSidebar]   = useState(false);
  const [alertsFor,     setAlertsFor]     = useState(null);
  const [stockPage,     setStockPage]     = useState(null); // { symbol, name, price } | null
  // Onboarding gate: backed by Supabase, NOT localStorage. The questionnaire
  // is shown only when the authenticated user has no row in investor_profiles.
  //   null  → still checking (render nothing — avoids a flash on slow networks)
  //   true  → no row found, show the flow
  //   false → row exists, skip the flow
  const [showOnboarding, setShowOnboarding] = useState(null);
  // While the questionnaire is hidden: the optional name screen on its own, for
  // a new user (no investor_profiles row) who has neither given a name nor
  // skipped the screen before (components/OnboardingFlow.jsx, NameOnlyFlow).
  const [showNameOnly,   setShowNameOnly]   = useState(false);
  const [displayStyle,   setDisplayStyle]   = useState("balanced"); // home-tab info ordering
  const [isAdmin,        setIsAdmin]        = useState(false);
  const [showAdmin,      setShowAdmin]      = useState(false);

  // resetProfile is called from Settings ("חזור על השאלון"). Wipe the Supabase
  // row first so the gate effect re-evaluates to "show", then clear the local
  // cache for backwards compatibility with any older code path still reading it.
  const resetProfile = async () => {
    // Hidden (constants/features.js): restarting DELETES the stored answers,
    // and they stay while the profile is off. Nothing calls this then.
    if (!INVESTOR_PROFILE_ENABLED) return;
    if (session?.user?.id) {
      try {
        const { error } = await supabase.from('investor_profiles').delete().eq('user_id', session.user.id);
        if (error) console.error('[App] resetProfile delete failed:', error.code, error.message);
      } catch (e) {
        console.error('[App] resetProfile unexpected error:', e?.message);
      }
    }
    localStorage.removeItem(PROFILE_KEY);
    setShowOnboarding(true);
  };

  // Single source of truth: ask Supabase whether this user has completed the
  // questionnaire. Runs on every session change (login / token refresh).
  //
  // Mobile race condition: useAuth() updates the React `session` prop as
  // soon as it hears about the user, but the Supabase SDK itself may not
  // have hydrated the JWT into its internal client state yet. If we fire
  // the query in that window, PostgREST RLS silently hides the row and
  // maybeSingle() returns { data: null, error: null } — indistinguishable
  // from "no row exists" — and we show the questionnaire to a user who
  // already completed it. To fix, we poll supabase.auth.getSession() until
  // the SDK confirms the same user.id as the React prop, then query.
  useEffect(() => {
    if (!session?.user?.id) return;
    // Guests skip the questionnaire entirely — the demo experience is ready-made.
    if (isGuest) { setShowOnboarding(false); return; }
    let alive = true;

    const waitForSdkSession = async (uid, maxWaitMs = 2500) => {
      const start = Date.now();
      while (alive && Date.now() - start < maxWaitMs) {
        const { data: { session: live } } = await supabase.auth.getSession();
        if (live?.user?.id === uid) return true;
        await new Promise(r => setTimeout(r, 100));
      }
      return false;
    };

    (async () => {
      try {
        const ready = await waitForSdkSession(session.user.id);
        if (!alive) return;
        if (!ready) {
          console.warn('[App] SDK session never hydrated for uid', session.user.id, '— skipping questionnaire as a fail-safe');
          // Fail-safe: don't trap the user behind the flow if auth is broken.
          // They can always re-open from Settings.
          setShowOnboarding(false);
          return;
        }

        const { data, error } = await supabase
          .from('investor_profiles')
          .select('user_id, display_style')
          .eq('user_id', session.user.id)
          .maybeSingle();
        if (!alive) return;
        if (error) {
          console.error('[App] investor_profiles check failed:', error.code, error.message);
          setShowOnboarding(false);
          return;
        }
        // Hidden (constants/features.js): no questionnaire for anyone, and the
        // stored display style is not applied · every user gets the default.
        if (!INVESTOR_PROFILE_ENABLED) {
          // Read from the SDK's own session (hydrated above), not the prop, so
          // the gate needs no extra dependency and sees the latest metadata.
          const { data: { session: live } } = await supabase.auth.getSession();
          if (!alive) return;
          const meta = live?.user?.user_metadata || {};
          const hasName = typeof meta.full_name === "string" && meta.full_name.trim() !== "";
          setShowNameOnly(!data && !hasName && !meta.name_prompted);
          setShowOnboarding(false);
          return;
        }
        if (data?.display_style) setDisplayStyle(data.display_style);
        setShowOnboarding(!data);
      } catch (e) {
        console.error('[App] investor_profiles unexpected error:', e?.message);
        if (alive) setShowOnboarding(false);
      }
    })();
    return () => { alive = false; };
  }, [session?.user?.id, isGuest]);

  const [lang, setLang] = useState(() => localStorage.getItem("si_lang") || "he");
  const t = TR[lang];
  const guestName = lang === "en" ? "Guest" : "אורח";
  // One name for the whole shell: guest label, or the name the account set.
  // Empty when the account never set one — callers greet without a name.
  const firstName = isGuest ? guestName : getFirstName(session);
  const toggleLang = () => {
    const next = lang === "he" ? "en" : "he";
    setLang(next);
    localStorage.setItem("si_lang", next);
  };

  useEffect(() => {
    document.documentElement.dir  = lang === "en" ? "ltr" : "rtl";
    document.documentElement.lang = lang;
  }, [lang]); // null=closed, ""=all, "SYMBOL"=specific

  // Scroll resets: top of page on every tab switch and on every overlay
  // open/close, so no screen ever opens mid-scroll.
  useEffect(() => { window.scrollTo(0, 0); }, [tab]);
  useEffect(() => { window.scrollTo(0, 0); }, [showAcademy, showCalc, showQA, showProfile, showSettings, showSimulator, showAdmin, showExposure, showScenario]);

  // Global stock-page opener: any component dispatches
  //   window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol, name, price } }))
  useEffect(() => {
    const open = (e) => { if (e.detail?.symbol) setStockPage(e.detail); };
    window.addEventListener("si-open-stock", open);
    return () => window.removeEventListener("si-open-stock", open);
  }, []);

  // ─── The shell fades in immediately ────────────────────────────────────────
  // `mounted` used to be set in init()'s `finally`, which meant the WHOLE app
  // — header, navigation, every card — sat at opacity 0 until the last call
  // of the launch chain came back. On a phone that was 7.5 seconds of blank
  // screen (measured 2026-09-23) while the content had been in the DOM since
  // 3.3s. The fade is a fade, not a loading state: every card below carries
  // its own skeleton and fills itself in.
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!session?.user?.id) return;
    const init = async () => {
      // Verify the SDK has a live session before hitting the DB
      const { data: { session: liveSession } } = await supabase.auth.getSession();
      console.debug('[App] init — uid:', session.user.id, '| sdk session uid:', liveSession?.user?.id ?? 'NULL');

      try {
        // The profile row and the portfolio are independent reads — they used
        // to run one after the other, and the admin role was a THIRD read of
        // the row the first one had already fetched.
        const [profileRow, pf] = await Promise.all([
          ensureProfile(session.user.id),
          loadOrCreatePortfolio(session.user.id),
        ]);
        if (profileRow?.role === 'admin') setIsAdmin(true);
        // Milestone 1 — signup completed. The profiles row is created by the
        // sign-up trigger, so a row minted in the last ten minutes IS the
        // sign-up: it counts once, on the server's clock, whichever flow
        // brought the session (immediate, or a confirmation mail opened
        // later). Guests are not signups.
        if (!isGuest && profileRow?.created_at && Date.now() - Date.parse(profileRow.created_at) < 10 * 60 * 1000) {
          trackEvent(EVENTS.SIGNUP_COMPLETED, { once: session.user.id });
        }
        // Where they came from, written once (migrations/sprint7.sql).
        saveAttribution(session.user.id, { isGuest });
        setPortfolioId(pf.id);
        let positions = await loadPositions(pf.id);

        // First guest login: seed a demo portfolio so every screen is populated.
        // Positions persist for this anonymous session; the watchlist is client
        // state, so it is (re)seeded on each guest load below.
        if (isGuest && positions.length === 0) {
          try {
            await seedDemoPositions(pf.id);
            positions = await loadPositions(pf.id);
          } catch (e) {
            console.error('[App] demo seed failed:', e?.code, e?.message);
          }
        }
        if (isGuest) {
          // No price until the quote lands — null renders "—", never a placeholder
          // number that looks like a quote.
          setWatchlist(DEMO_WATCHLIST.map(sym => ({ symbol: sym, name: sym, currentPrice: null, change: null })));
        } else {
          // A registered user's own list (sprint 13, 5.8 · lib/watchlistService.js).
          loadWatchlist().then(({ rows, unavailable, error }) => {
            setWatchlistSaving(!unavailable && !error);
            setWatchlist(rows.map(sym => ({ symbol: sym, name: sym, currentPrice: null, change: null })));
            // Prices now, not at the next 30-second refresh.
            if (rows.length) fetchQuotes(rows).then(quotes => {
              for (const [sym, q] of Object.entries(quotes || {})) {
                if (!q || !(q.c > 0)) continue;
                const stale = isStaleQuote(q.t);
                setWatchlist(w => w.map(s => s.symbol === sym ? { ...s, currentPrice: stale ? null : q.c, change: stale ? null : q.dp, lastQuoteAt: stale ? q.t : null } : s));
              }
            });
          });
        }

        const items = positions.map(p => ({
          positionId:   p.id,
          symbol:       p.symbol,
          name:         p.symbol,
          shares:       p.quantity,
          buyPrice:     p.buy_price,
          buyDate:      p.buy_date || null,
          currentPrice: p.buy_price,
          quote:        "pending",
          // TICKER_SECTORS הוא ערך פתיחה בלבד — תגית מיידית בלי קריאת רשת.
          // הסיווג החי מול profile2 רץ על כל ההחזקות מיד אחרי הטעינה ודורס
          // אותו; עד אז sector נשאר undefined למי שאינו בטבלה, והתגית לא מוצגת.
          sector:       TICKER_SECTORS[p.symbol],
          industry:     null,
        }));
        setPortfolio(items);
        setPortfolioReady(true);
        // From here on the screen is rendered and every remaining call fills
        // a card that is already on it. Nothing below is awaited by anything
        // above, so nothing below runs in series with it.

        // Load persisted price alerts (RLS-scoped to this user).
        loadAlerts(session.user.id).then(setAlerts).catch(() => {});
        // The marking layer (holding_segments) — module store, read by the
        // holding card, the geo list and the home banner. Not awaited: nothing
        // above the fold depends on it.
        loadHoldingMarks(session.user.id).catch(() => {});

        // Fetch real-time prices immediately after Supabase load.
        // Without this, currentPrice stays at buy_price until the 30-second
        // polling interval fires — which would show stale/wrong prices on first render.
        // One request for the whole portfolio. This used to be a sequential
        // loop with a 150ms gap between symbols, because sixty requests a
        // minute is the free tier and a browser firing ten at once eats a sixth
        // of it. Ten holdings therefore cost ten round trips plus 1.35 seconds
        // of deliberate waiting before the first real price appeared.
        // The proxy fans out server-side, so the rate limit is respected in one
        // place and the screen gets its prices in a single trip.
        // A holding the FIRST round left unanswered (the proxy failed, or a
        // rate limit) is shown as "no price" rather than left pending: pending
        // kept the value card on its skeleton for as long as the failure
        // lasted, with nothing on screen to say why (2026-09-26). The 30s poll
        // still fills it the moment an answer arrives (withQuote, q.c > 0).
        const unanswered = s => (s.quote === "pending" ? { ...s, currentPrice: null, prevClose: null, quote: "none" } : s);
        // Only the symbols of THIS round: a holding added while it was in
        // flight has its own request and is not this round's to judge.
        const asked = new Set(items.map(s => s.symbol));
        fetchQuotes([...asked]).then(quotes => {
          setPortfolio(p => p.map(s => {
            if (!asked.has(s.symbol)) return s;
            const q = quotes[s.symbol];
            return q ? withQuote(s, q) : unanswered(s);
          }));
        }).catch(() => { setPortfolio(p => p.map(s => (asked.has(s.symbol) ? unanswered(s) : s))); });

        // Resolve the live sector for every holding — including the ones
        // TICKER_SECTORS pre-tagged: the table is a seed, and a live answer
        // outranks every hand-written table, always (same rule as addStock).
        //
        // Still one at a time with a gap, for the reason it always was: the
        // free tier allows 60 requests a minute and this shares that budget
        // with the quote and news calls. What changed is that it no longer
        // runs IN FRONT of the first paint — it is a background fill for a
        // tag on a card that is already on screen, so its four round trips
        // plus 600ms of deliberate waiting cost the user nothing.
        (async () => {
          for (const sym of items.map(s => s.symbol)) {
            // A sector answered before is an answer still: the cache holds
            // only what the live lookup returned, for thirty days
            // (utils/sectorCache.js). Most launches now make no profile call
            // at all, and the ones that do are for holdings just added.
            const cached = getCachedSector(sym);
            if (cached) {
              setPortfolio(p => p.map(s => s.symbol === sym
                ? { ...s, sector: cached.sector, industry: cached.industry, name: cached.name || s.name }
                : s));
              continue;
            }
            // eslint-disable-next-line no-await-in-loop
            const { sector, industry, name } = await classifySymbol(sym, fetchProfile);
            // A null sector means the request failed. Leave the holding
            // untagged rather than asserting "אחר" on a network error, and
            // do not cache a failure.
            if (sector) {
              setCachedSector(sym, { sector, industry, name });
              setPortfolio(p => p.map(s => s.symbol === sym ? { ...s, sector, industry, name: name || s.name } : s));
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise(r => setTimeout(r, 150));
          }
        })();
      } catch (err) {
        console.error('[App] Portfolio load failed — code:', err?.code, '| message:', err?.message);
        // A failed load is an answer too: the card must stop waiting.
        setPortfolioReady(true);
      }
    };
    init();
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track current symbols so the interval always sees the latest list
  const allSymsRef = useRef([]);
  useEffect(() => {
    allSymsRef.current = [
      ...portfolio.map(s => s.symbol),
      ...watchlist.map(s => s.symbol),
    ];
  }, [portfolio, watchlist]);

  // Poll real prices every 30 seconds — one request for every symbol, not one
  // per symbol. The rate limit is now enforced on the server, where it belongs:
  // the browser no longer needs to pace itself to protect a shared quota.
  useEffect(() => {
    const refresh = async () => {
      const quotes = await fetchQuotes(allSymsRef.current);
      for (const [sym, q] of Object.entries(quotes)) {
        if (!q) continue;
        setPortfolio(p => p.map(s => s.symbol === sym ? withQuote(s, q) : s));
        if (!(q.c > 0)) continue;
        // A stopped quote is no price for the watchlist either (5.5).
        const stale = isStaleQuote(q.t);
        setWatchlist(w => w.map(s => s.symbol === sym ? { ...s, currentPrice: stale ? null : q.c, change: stale ? null : q.dp, lastQuoteAt: stale ? q.t : null } : s));
      }
    };
    refresh();
    const id = setInterval(refresh, 30 * 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!settings.alertsEnabled || !alerts.length) return;
    const all = [...portfolio, ...watchlist];
    setTriggered(alerts.filter(a => { const s = all.find(x => x.symbol === a.symbol); return s && s.currentPrice > 0 && (a.direction === "above" ? s.currentPrice >= a.targetPrice : s.currentPrice <= a.targetPrice); }));
  }, [portfolio, watchlist, alerts, settings.alertsEnabled]);

  // Returns true when a position was added, false for a duplicate — the
  // portfolio screen asks its one question only for a holding that is new.
  const addPortfolio = async ({ symbol, shares, buyPrice, buyDate }) => {
    if (portfolio.find(s => s.symbol === symbol)) return false;
    let positionId = null;
    if (portfolioId) {
      try {
        positionId = await addPosition(portfolioId, {
          symbol,
          quantity:  shares,
          buy_price: buyPrice,
          buy_date:  buyDate || null,
        });
      } catch (err) {
        console.error("addPosition error:", err);
      }
    }
    // TICKER_SECTORS הוא ערך פתיחה בלבד — תגית מיידית עד שהסיווג החי חוזר,
    // והתשובה החיה דורסת אותו תמיד.
    // Milestone 2 — the moment of value: a portfolio exists. Fired on the
    // FIRST holding of a real account, whether typed in or imported from a
    // CSV (both paths land here). A guest's demo portfolio is seeded, not
    // entered, and is not this.
    if (!isGuest && portfolio.length === 0) {
      trackEvent(EVENTS.FIRST_PORTFOLIO_ENTERED, { once: session?.user?.id || "anon" });
    }
    setPortfolio(p => [...p, { positionId, symbol, name: symbol, shares, buyPrice, buyDate: buyDate || null, currentPrice: buyPrice, quote: "pending", sector: TICKER_SECTORS[symbol], industry: null }]);
    classifySymbol(symbol, fetchProfile).then(({ sector, industry, name }) => {
      if (!sector) return;
      setCachedSector(symbol, { sector, industry, name });
      setPortfolio(p => p.map(s => s.symbol === symbol ? { ...s, sector, industry, name: name || s.name } : s));
    });
    // Same rule as the first round on load: a holding the quote call left
    // unanswered is "no price", not pending forever. Pending holds back every
    // weight and "מה השתנה" (sprint 14, 3.1, review); the 30s poll fills it.
    const unanswered = s => (s.quote === "pending" ? { ...s, currentPrice: null, prevClose: null, quote: "none" } : s);
    fetchQuote(symbol)
      .then(q => { setPortfolio(p => p.map(s => (s.symbol === symbol ? (q ? withQuote(s, q) : unanswered(s)) : s))); })
      .catch(() => { setPortfolio(p => p.map(s => (s.symbol === symbol ? unanswered(s) : s))); });
    return true;
  };

  const removePortfolioStock = async (sym) => {
    const stock = portfolio.find(s => s.symbol === sym);
    if (stock?.positionId) {
      try { await removePosition(stock.positionId); } catch (err) { console.error("removePosition error:", err); }
    }
    setPortfolio(p => p.filter(x => x.symbol !== sym));
    // A sold holding takes its mark with it.
    removeHoldingMark(sym).catch(() => {});
  };
  // Persist price alerts to Supabase, then mirror into state. The DB assigns
  // the id (uuid), so we use the returned row rather than the drawer's temp id.
  // Returns whether the alert was saved, so the drawer can say so when it was
  // not (sprint 14, 4.4 · a failed insert used to leave the drawer silent).
  const addAlertHandler = async (a) => {
    const saved = await addAlert(session?.user?.id, {
      symbol: a.symbol, direction: a.direction, targetPrice: a.targetPrice,
    });
    if (saved) setAlerts(al => [...al, saved]);
    return !!saved;
  };
  const removeAlertHandler = (id) => {
    removeAlert(id);
    setAlerts(al => al.filter(a => a.id !== id));
  };

  // Add and remove, for a registered user (sprint 13, 5.8). The list feeds the
  // news and geo relevance ("במעקב שלך") and the quote refresh above. A guest
  // keeps the demo list; saving is off until the table exists
  // (migrations/sprint13.sql), and the screen says so.
  const [watchlistSaving, setWatchlistSaving] = useState(false);
  const addWatchlist = async (raw) => {
    const symbol = String(raw || "").trim().toUpperCase();
    if (!symbol || isGuest || !watchlistSaving) return false;
    if (watchlist.some(w => w.symbol === symbol) || portfolio.some(p => p.symbol === symbol)) return false;
    const res = await addWatch(session?.user?.id, symbol);
    if (res.error) { if (res.unavailable) setWatchlistSaving(false); return { failed: true }; }
    setWatchlist(w => [...w, { symbol, name: symbol, currentPrice: null, change: null }]);
    fetchQuote(symbol).then(q => {
      // Same rule as the refresh: a quote stale by more than ten days is not a
      // price today, and its move is not shown (sprint 14, review).
      if (q && q.c > 0) {
        const stale = isStaleQuote(q.t);
        setWatchlist(w => w.map(s => s.symbol === symbol ? { ...s, currentPrice: stale ? null : q.c, change: stale ? null : q.dp, lastQuoteAt: stale ? q.t : null } : s));
      }
    });
    return true;
  };
  const removeWatchlist = async (symbol) => {
    if (isGuest || !watchlistSaving) return false;
    const res = await removeWatch(symbol);
    if (res.error) { if (res.unavailable) setWatchlistSaving(false); return false; }
    setWatchlist(w => w.filter(s => s.symbol !== symbol));
    return true;
  };
  const watch = { list: watchlist, canEdit: !isGuest && watchlistSaving, isGuest, add: addWatchlist, remove: removeWatchlist };

  const isDesktop = useIsDesktop();
  const [animTab, setAnimTab] = useState(null);
  const [visited, setVisited] = useState(() => new Set(["home"]));

  // A tab switch that carries a request with it: the home screen asking the
  // market intelligence page to open one geopolitical insight (intelRequest,
  // sprint 20), and that page asking the news list for a filter (newsRequest).
  //
  // This is state and not a DOM event on purpose. It used to be a CustomEvent
  // fired straight after setTab, and the listener lives inside NewsTab, which
  // is not mounted until React renders the new tab — so the event was dispatched
  // into an empty room and the request vanished. On desktop that was every
  // single time; on mobile only the first, since panels stay mounted once
  // visited. State does not need the destination to already exist: NewsTab
  // reads it whenever it mounts, and clears it once acted on.
  //
  // `seq` makes a repeat request distinguishable from one already handled —
  // tapping the same insight twice must reopen it.
  const [newsRequest, setNewsRequest] = useState(null);
  const [intelRequest, setIntelRequest] = useState(null);
  const newsSeq = useRef(0);
  const touchStartRef = useRef(null);
  const trackRef      = useRef(null);
  const carouselRef   = useRef(null);
  const tabRef        = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  // All mobile panels live side by side in one flex row, so the row's height —
  // and therefore the page's scroll height — used to be the height of the
  // TALLEST visited panel. On a short tab (home) the page kept scrolling long
  // after its content ended. Fix: only the active panel contributes its real
  // height; the others are clamped to it and clipped. The clamp is measured
  // after paint, so during a tab transition it still holds the outgoing
  // panel's own height and nothing is visibly cut mid-slide.
  const panelRefs     = useRef({});
  const [activePanelH, setActivePanelH] = useState(null);
  useEffect(() => {
    if (isDesktop) { setActivePanelH(null); return; }
    const el = panelRefs.current[tab];
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setActivePanelH(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isDesktop, tab]);

  useEffect(() => {
    if (isDesktop) return;
    const wrapper = carouselRef.current;
    const track   = trackRef.current;
    if (!wrapper || !track) return;

    // A tab-swipe must never hijack a horizontal gesture that belongs to the
    // content itself — a chart pan or any horizontally-scrollable strip. If the
    // touch starts inside such an element, we don't arm the tab swipe.
    const startsInHScroll = (target) => {
      let el = target;
      while (el && el !== wrapper) {
        if (el.nodeType === 1) {
          if (el.classList?.contains("recharts-wrapper") || el.hasAttribute?.("data-noswipe")) return true;
          const cs = window.getComputedStyle(el);
          if (/(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 4) return true;
        }
        el = el.parentNode;
      }
      return false;
    };

    const onStart = (e) => {
      if (startsInHScroll(e.target)) { touchStartRef.current = null; return; }
      touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now(), locked: false, cancelled: false };
    };

    // Direction is decided ONCE, and only after the finger has travelled far
    // enough for the direction to be real. The previous version compared dx/dy
    // on the very first touchmove — where both are 1-3px — and cancelled the
    // gesture permanently on any vertical drift. That is why a swipe felt dead
    // in the middle of a long page (news): a finger arriving off a vertical
    // scroll almost always drifts vertically in its first sample, so the tab
    // swipe was killed before it began. Vertical scrolling is unaffected:
    // below the dead zone nothing is decided and nothing is preventDefault-ed,
    // and a vertical-dominant gesture is released to the browser as before.
    const SWIPE_DEADZONE = 12; // px
    const onMove = (e) => {
      const st = touchStartRef.current;
      if (!st || st.cancelled) return;
      const dx = Math.abs(e.touches[0].clientX - st.x);
      const dy = Math.abs(e.touches[0].clientY - st.y);
      if (!st.locked) {
        if (Math.max(dx, dy) < SWIPE_DEADZONE) return; // still ambiguous
        if (dx > dy * 1.2) st.locked = true;
        else { st.cancelled = true; return; }
      }
      e.preventDefault();
    };

    const onEnd = (e) => {
      if (!touchStartRef.current || touchStartRef.current.cancelled) { touchStartRef.current = null; return; }
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
      const dt = Date.now() - touchStartRef.current.t;
      touchStartRef.current = null;
      if (Math.abs(dx) < 80 || Math.abs(dy) > Math.abs(dx) || Math.abs(dx) / dt < 0.2) return;
      const curIdx = MOBILE_TAB_IDS.indexOf(tabRef.current);
      if (curIdx === -1) return;
      const snap = (idx) => {
        track.style.transition = 'transform 0.3s ease';
        track.style.transform  = `translateX(${idx * 100}vw)`;
        setTimeout(() => { if (trackRef.current) trackRef.current.style.transition = ''; }, 300);
      };
      // RTL convention (Investing-style): finger RIGHT = next tab. The track is
      // laid right-to-left, so the positive translateX moves WITH the finger.
      if (dx > 0 && curIdx < MOBILE_TAB_IDS.length - 1) {
        const ni = curIdx + 1;
        snap(ni);
        setTab(MOBILE_TAB_IDS[ni]);
        setAnimTab(MOBILE_TAB_IDS[ni]);
        setVisited(v => { const n = new Set(v); n.add(MOBILE_TAB_IDS[ni]); return n; });
        setTimeout(() => setAnimTab(null), 450);
      } else if (dx < 0 && curIdx > 0) {
        const pi = curIdx - 1;
        snap(pi);
        setTab(MOBILE_TAB_IDS[pi]);
        setAnimTab(MOBILE_TAB_IDS[pi]);
        setVisited(v => { const n = new Set(v); n.add(MOBILE_TAB_IDS[pi]); return n; });
        setTimeout(() => setAnimTab(null), 450);
      }
    };

    wrapper.addEventListener('touchstart', onStart, { passive: true });
    wrapper.addEventListener('touchmove',  onMove,  { passive: false });
    wrapper.addEventListener('touchend',   onEnd,   { passive: true });
    return () => {
      wrapper.removeEventListener('touchstart', onStart);
      wrapper.removeEventListener('touchmove',  onMove);
      wrapper.removeEventListener('touchend',   onEnd);
    };
  }, [isDesktop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (document.getElementById("si-nav-style")) return;
    const s = document.createElement("style");
    s.id = "si-nav-style";
    s.textContent = `
      @keyframes navPop {
        0%   { transform: scale(1)    translateY(0); }
        25%  { transform: scale(1.4)  translateY(-6px); }
        55%  { transform: scale(0.88) translateY(0); }
        75%  { transform: scale(1.12) translateY(-2px); }
        100% { transform: scale(1)    translateY(0); }
      }
      .nav-icon-anim { animation: navPop 0.42s cubic-bezier(.36,.07,.19,.97) both; }
    `;
    document.head.appendChild(s);
  }, []);

  // On mobile, every screen that is not one of the five carousel panels is a
  // full-screen overlay with a flag of its own. One map from id to flag, used
  // by the drawer and by handleTabClick alike, so a link that says
  // onNavigate("exposure") reaches the screen on a phone as well as on a
  // desktop. Before this, "exposure" set `tab` to a panel the carousel does
  // not hold and nothing appeared.
  const MOBILE_OVERLAYS = {
    ...(ACADEMY_ENABLED ? { academy: setShowAcademy } : {}),
    ...(CALCULATORS_ENABLED ? { calculators: setShowCalc } : {}),
    qa:          setShowQA,
    ...(INVESTOR_PROFILE_ENABLED ? { profile: setShowProfile } : {}),
    settings:    setShowSettings,
    simulator:   setShowSimulator,
    exposure:    setShowExposure,
    whatif:      setShowScenario,
    admin:       setShowAdmin,
  };
  const openMobileScreen = (id) => {
    const open = MOBILE_OVERLAYS[id];
    if (open) { open(true); trackScreen(id); }
  };

  // Screen-open measurement: the active carousel/desktop tab, on every change
  // (click, swipe, or a card's onNavigate). Overlays are counted where they
  // open, above. Once a session exists — a screen with no user is not a row.
  useEffect(() => {
    if (session?.user?.id) trackScreen(tab);
  }, [tab, session?.user?.id]);

  const handleTabClick = (id, opts) => {
    const ni = MOBILE_TAB_IDS.indexOf(id);
    if (!isDesktop && ni === -1) { openMobileScreen(id); return; }
    // The geopolitical view moved from the news screen to the market
    // intelligence page (sprint 20, stage 3): a request for it lands there.
    if (opts && "geoFocus" in opts) {
      newsSeq.current += 1;
      setIntelRequest({ focusUrl: opts.geoFocus ?? null, seq: newsSeq.current });
    }
    // The news list on a given filter · "לכל החדשות על התיק" in the market
    // intelligence page (sprint 20) opens it on "נוגע לתיק שלי".
    if (opts && opts.newsCategory) {
      newsSeq.current += 1;
      setNewsRequest({ mode: "news", category: opts.newsCategory, seq: newsSeq.current });
    }
    setTab(id);
    setAnimTab(id);
    setVisited(v => { const n = new Set(v); n.add(id); return n; });
    setTimeout(() => setAnimTab(null), 450);
    if (!isDesktop && trackRef.current && ni !== -1) {
      trackRef.current.style.transition = 'transform 0.28s cubic-bezier(0.32,0.72,0,1)';
      trackRef.current.style.transform  = `translateX(${ni * 100}vw)`;
      setTimeout(() => { if (trackRef.current) trackRef.current.style.transition = ''; }, 280);
    }
  };

  // Global academy opener: any component dispatches
  //   window.dispatchEvent(new CustomEvent("si-open-academy"))
  // and the /academy path lands here too (index.js no longer redirects it;
  // netlify.toml serves the app for it). Desktop switches to the Academy tab,
  // a phone opens its full-screen overlay. Off while ACADEMY_ENABLED is false.
  // Before 28/09/2026 the event only opened the phone overlay, so the button
  // on "מושג השבוע" did nothing on a desktop.
  const openAcademyRef = useRef(null);
  openAcademyRef.current = () => {
    if (isDesktop) handleTabClick("academy");
    else setShowAcademy(true);
  };
  useEffect(() => {
    if (!ACADEMY_ENABLED) return undefined;
    const open = () => openAcademyRef.current?.();
    window.addEventListener("si-open-academy", open);
    if (/^\/academy(\/|$)/i.test(window.location.pathname)) open();
    return () => window.removeEventListener("si-open-academy", open);
  }, []);

  // "מה אם" from the company page's "בדוק בתרחיש" (sprint 13, stage 3):
  //   openScenarioWith(symbol)  →  window event "si-open-scenario"
  // The company page closes and the screen opens · a tab on a desktop, the
  // overlay on a phone. The symbol itself waits in utils/scenario.js until the
  // screen takes it, whichever of the two mounts first.
  const openScenarioRef = useRef(null);
  openScenarioRef.current = () => {
    setStockPage(null);
    if (isDesktop) handleTabClick("whatif");
    else setShowScenario(true);
  };
  useEffect(() => {
    const open = () => openScenarioRef.current?.();
    window.addEventListener("si-open-scenario", open);
    return () => window.removeEventListener("si-open-scenario", open);
  }, []);

  const renderMobileTab = (id) => {
    switch (id) {
      case "home":      return <HomeTab portfolio={portfolio} portfolioReady={portfolioReady} userId={session?.user?.id} watchlist={watchlist} isGuest={isGuest} firstName={firstName} cur={cur} displayStyle={displayStyle} onNavigate={(tabId, opts) => handleTabClick(tabId, opts)}/>;
      case "portfolio": return <PortfolioTab portfolio={portfolio} cur={cur} onAdd={addPortfolio} onRemove={removePortfolioStock} onOpenAlerts={PRICE_ALERTS_ENABLED ? sym => setAlertsFor(sym) : undefined} lang={lang} watch={watch}/>;
      case "news":      return <NewsTab portfolio={portfolio} watchlist={watchlist} lang={lang} request={newsRequest} onRequestHandled={() => setNewsRequest(null)}/>;
      case "calendar":  return <CalendarTab lang={lang} portfolio={portfolio} watchlist={watchlist}/>;
      case "ai":        return <AIAnalysisTab isPro={isPro} onUnlockPro={() => setIsPro(true)} onOpenAlerts={PRICE_ALERTS_ENABLED ? sym => setAlertsFor(sym) : undefined} lang={lang} portfolio={portfolio} portfolioReady={portfolioReady} watchlist={watchlist} onNavigate={(tabId, opts) => handleTabClick(tabId, opts)} request={intelRequest} onRequestHandled={() => setIntelRequest(null)}/>;
      case "exposure":  return <ExposureTab portfolio={portfolio} cur={cur}/>;
      default: return null;
    }
  };

  const TABS = [
    { id: "home",      label: lang==="en"?"Home":       "דף הבית",   Icon: (p) => <IconHome {...p}/> },
    { id: "portfolio", label: lang==="en"?"Portfolio":  "תיק", Icon: (p) => <IconPortfolio {...p}/> },
    { id: "exposure",  label: lang==="en"?"Portfolio exposures": "חשיפות התיק", Icon: (p) => <IconGlobe {...p}/>, mobileHide: true },
    { id: "whatif",    label: lang==="en"?"What if": "מה אם", Icon: (p) => <IconScenario {...p}/>, mobileHide: true },
    { id: "news",      label: lang==="en"?"News":       "חדשות",     Icon: (p) => <IconNews {...p}/> },
    { id: "calendar",  label: lang==="en"?"Calendar":   "יומן",      Icon: (p) => <IconCalendar {...p}/> },
    { id: "ai",        label: lang==="en"?"AI Analysis":"מודיעין שוק",  Icon: (p) => <IconAI {...p}/> },
    ...(INVESTOR_PROFILE_ENABLED ? [{ id: "profile",   label: lang==="en"?"My Profile": "הפרופיל שלי", Icon: (p) => <IconProfile   {...p}/>, mobileHide: true }] : []),
    { id: "simulator", label: lang==="en"?"Practice account": "חשבון תרגול",  Icon: (p) => <IconSimulator {...p}/>, mobileHide: true },
    ...(ACADEMY_ENABLED ? [{ id: "academy",     label: lang==="en"?"Academy":    "אקדמיה",   Icon: (p) => <IconAcademy {...p}/>,     mobileHide: true }] : []),
    ...(CALCULATORS_ENABLED ? [{ id: "calculators", label: lang==="en"?"Calculators":"מחשבונים", Icon: (p) => <IconCalculator {...p}/>,  mobileHide: true }] : []),
    { id: "qa",          label: lang==="en"?"Q&A":        "שאלות נפוצות",    Icon: (p) => <IconQA {...p}/>,          mobileHide: true },
    { id: "settings",    label: lang==="en"?"Settings":   "הגדרות",   Icon: (p) => <IconSettings {...p}/>,    mobileHide: true },
  ];

  return (
    <div dir={lang === "en" ? "ltr" : "rtl"} data-lang={lang}
      style={{ minHeight: "100dvh", background: UI.surface.page, fontFamily: FONT.body, color: UI.text.primary, paddingBottom: isDesktop ? STATUS_BAR_H : MOBILE_BOTTOM_PAD, opacity: mounted ? 1 : 0, transition: "opacity .4s", fontSize: 15, fontWeight: 500 }}>
      <TickerTape/>

      {/* Guest-mode banner — thin, permanent, sits between the ticker (top:0,h30)
          and the header. In guest mode the header drops by GUEST_BANNER_H so all
          three stack without overlap. */}
      {isGuest && (
        <div style={{
          position: "sticky", top: tickerH, zIndex: 100,
          height: GUEST_BANNER_H, boxSizing: "border-box",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: UI.surface.sunken, borderBottom: `1px solid ${UI.line.hairline}`,
          color: UI.text.secondary, fontSize: 12.5, fontWeight: 700,
          marginRight: isDesktop ? 220 : 0, padding: "0 12px",
        }}>
          {lang === "en" ? "Guest mode · sample portfolio · real data · information only" : "מצב אורח · תיק לדוגמה · נתונים אמיתיים · מידע בלבד"}
        </div>
      )}

      {/* Header */}
      <div className="si-glass" style={{ borderBottom: `1px solid ${UI.line.hairline}`, padding: isMobile ? "10px 16px" : "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: isGuest ? tickerH + GUEST_BANNER_H : tickerH, zIndex: 100, marginRight: isDesktop ? 220 : 0 }}>
        <div onClick={() => handleTabClick("home")} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <img src={logo256} alt="Suricata" style={{ width: isMobile ? 38 : 44, height: isMobile ? 38 : 44, borderRadius: "22%", display: "block", background: UI.logoPlate, objectFit: "cover" }}/>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span dir="ltr" style={{ fontWeight: 700, fontSize: isMobile ? 17 : 22, color: UI.text.primary, letterSpacing: "-0.5px", fontFamily: FONT.latin, unicodeBidi: "isolate" }}>Suricata</span>
              {/* The "PRO" badge that sat here is gone until there is a paid tier (ROADMAP 7.4). isPro stays. */}
            </div>
            <div style={{ fontSize: isMobile ? 13 : 14, color: UI.text.secondary, fontWeight: 600, opacity: .85 }}>{greetingWithName(firstName, lang)}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {PRICE_ALERTS_ENABLED && triggered.length > 0 && (
            <button onClick={() => setAlertsFor("")} aria-label={`התראות מחיר שהגיעו לסף · ${triggered.length}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: 6, padding: "4px 10px", minHeight: 44, fontSize: 13, color: UI.text.primary, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}><Icon name="bell" size={14} color={UI.text.primary}/>{triggered.length}</button>
          )}
          {/* Live indicator — desktop only, and only while the market is open */}
          {isDesktop && market.open && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 6, height: 6, background: UI.text.secondary, borderRadius: "50%", display: "inline-block" }}/>
              <span style={{ color: UI.text.secondary, fontWeight: 700, fontSize: 13, letterSpacing: ".5px" }}>LIVE</span>
            </div>
          )}
          {isDesktop && (
            <span dir="ltr" style={{ fontSize: 13, fontWeight: 600, color: UI.text.muted, letterSpacing: ".3px", unicodeBidi: "isolate" }}>{fxLabel}</span>
          )}
          {/* Flag language switcher — desktop only; on mobile language lives in
              Settings to keep the header to burger | logo | greeting. */}
          {isDesktop && (
            <div style={{ display:"flex", alignItems:"center", gap:2 }}>
              {[
                { code:"he", flag:"🇮🇱" },
                { code:"en", flag:"🇺🇸" },
              ].map(({ code, flag }) => (
                <button key={code} onClick={() => { if (lang !== code) toggleLang(); }}
                  style={{ background:"transparent", border:"none", borderRadius:6, padding:"4px 6px",
                           fontSize:20, cursor: lang===code ? "default" : "pointer",
                           opacity: lang===code ? 1 : 0.35, lineHeight:1,
                           outline: lang===code ? `1.5px solid ${UI.line.hairline}` : "none" }}>
                  {flag}
                </button>
              ))}
            </div>
          )}
          {/* Admin — desktop button; on mobile it lives in the sidebar menu */}
          {isAdmin && isDesktop && (
            <button onClick={() => setShowAdmin(true)} style={{ background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: 6, padding: "6px 10px", fontSize: 11, color: UI.text.primary, cursor: "pointer", fontWeight: 700, fontFamily: "inherit" }}>
              ניהול
            </button>
          )}
          {/* Day / night — one tap, same place on both devices, next to the
              last control in the header cluster. */}
          <ThemeToggle compact={!isMobile}/>

          {/* Desktop sign-out; Mobile burger */}
          {isDesktop ? (
            <button onClick={onSignOut} style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "6px 10px", fontSize: 11, color: UI.text.muted, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>
              {lang === "en" ? "Sign Out" : "יציאה"}
            </button>
          ) : (
            <button onClick={() => setShowSidebar(true)} aria-label={lang === "en" ? "Menu" : "תפריט"}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:8,
                       padding:"6px 10px", minWidth:44, minHeight:44, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6"  x2="21" y2="6"  stroke={UI.text.secondary} strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke={UI.text.secondary} strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="3" y1="18" x2="21" y2="18" stroke={UI.text.secondary} strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* התיק היום — the day's move in digits, as the first row of the content
          on the screens that have no value card of their own. */}
      {DAY_MOVE_TABS.includes(tab) && (
        <HorizonLine portfolio={portfolio} lang={lang}
          style={isDesktop
            ? { margin: "18px 40px 0", marginRight: 260 }   // clears the fixed right sidebar (220 + gutter)
            : { margin: "12px 16px 0" }}/>
      )}

      {/* Content — Desktop: simple render | Mobile: carousel track */}
      {isDesktop ? (
        <div style={{ padding: "24px 40px 0", marginRight: 220 }}>
          {tab === "home"      && <HomeTab portfolio={portfolio} portfolioReady={portfolioReady} userId={session?.user?.id} watchlist={watchlist} isGuest={isGuest} firstName={firstName} cur={cur} displayStyle={displayStyle} onNavigate={(tabId, opts) => handleTabClick(tabId, opts)}/>}
          {tab === "portfolio" && <PortfolioTab portfolio={portfolio} cur={cur} onAdd={addPortfolio} onRemove={removePortfolioStock} onOpenAlerts={PRICE_ALERTS_ENABLED ? sym => setAlertsFor(sym) : undefined} lang={lang} watch={watch}/>}
          {INVESTOR_PROFILE_ENABLED && tab === "profile"  && <MyProfileTab lang={lang} onResetProfile={resetProfile}/>}
          {tab === "simulator" && <SimAccountTab session={session} lang={lang}/>}
          {tab === "news"      && <NewsTab portfolio={portfolio} watchlist={watchlist} lang={lang} request={newsRequest} onRequestHandled={() => setNewsRequest(null)}/>}
          {tab === "calendar"  && <CalendarTab lang={lang} portfolio={portfolio} watchlist={watchlist}/>}
          {tab === "ai"        && <AIAnalysisTab isPro={isPro} onUnlockPro={() => setIsPro(true)} onOpenAlerts={PRICE_ALERTS_ENABLED ? sym => setAlertsFor(sym) : undefined} lang={lang} portfolio={portfolio} portfolioReady={portfolioReady} watchlist={watchlist} onNavigate={(tabId, opts) => handleTabClick(tabId, opts)} request={intelRequest} onRequestHandled={() => setIntelRequest(null)}/>}
          {tab === "exposure"  && <ExposureTab portfolio={portfolio} cur={cur}/>}
          {tab === "whatif"    && <ScenarioTab portfolio={portfolio} portfolioReady={portfolioReady}/>}
          {ACADEMY_ENABLED && tab === "academy" && <ChunkBoundary><Suspense fallback={<AcademyLoading/>}><AcademyTab lang={lang}/></Suspense></ChunkBoundary>}
          {CALCULATORS_ENABLED && tab === "calculators" && <CalculatorsTab lang={lang}/>}
          {tab === "qa"          && <QATab lang={lang}/>}
          {tab === "settings"    && <SettingsTab isPro={isPro} onUnlockPro={() => setIsPro(true)} cur={cur} setCur={setCur} lang={lang} toggleLang={toggleLang} t={t} portfolio={portfolio} onResetProfile={resetProfile} session={session}/>}
        </div>
      ) : (
        // RTL carousel: panels run right-to-left (home rightmost), so "next"
        // sits to the LEFT and a rightward finger-swipe pulls it in, with the
        // track translating in the finger's direction.
        <div ref={carouselRef} style={{ overflow: "hidden" }}>
          <div ref={trackRef} dir="rtl" style={{ display: "flex", alignItems: "flex-start", willChange: "transform" }}>
            {MOBILE_TAB_IDS.map(tabId => {
              const isActivePanel = tabId === tab;
              return (
                <div
                  key={tabId}
                  ref={el => { panelRefs.current[tabId] = el; }}
                  dir={lang === "he" ? "rtl" : "ltr"}
                  style={{
                    width: "100vw", flexShrink: 0,
                    padding: isMobile ? "14px 16px 0" : "24px 40px 0",
                    // Inactive panels must not stretch the page's scroll height.
                    ...(isActivePanel ? null : {
                      maxHeight: activePanelH != null ? activePanelH : "100dvh",
                      overflow: "hidden",
                    }),
                  }}
                >
                  {visited.has(tabId) && renderMobileTab(tabId)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Desktop Right Sidebar Nav.

          A fixed-height flex column: header, the nav list, the currency line,
          and the savanna at its foot. Only the nav list may flex — everything
          else keeps its size — and `minHeight: 0` on it is what makes that
          work at all. A flex item defaults to `min-height: auto`, which
          refuses to shrink below its content, so the list overflowed instead
          of scrolling and the platform clipped it: on a laptop the eleventh
          nav item was cut through the middle and the sidebar simply stopped,
          with no bottom to it. */}
      {isDesktop && (
        <div style={{ position: "fixed", right: 0, top: tickerH, bottom: 0, width: 220, background: UI.surface.raised, borderLeft: `1px solid ${UI.line.hairline}`, display: "flex", flexDirection: "column", zIndex: 99 }}>
          <div style={{ flexShrink: 0, padding: "16px 16px 12px", borderBottom: `1px solid ${UI.line.hairline}` }}>
            <div style={{ fontSize: 13, color: UI.text.secondary, fontWeight: 700, textAlign: "center", textTransform: "uppercase" }}>{t.navSection}</div>
          </div>
          <div style={{ flex: "1 1 0", minHeight: 0, padding: "8px 8px", display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>
            {TABS.map(t => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => handleTabClick(t.id)} style={{ background: active ? UI.state.surface : "transparent", border: "none", borderLeft: active ? `2px solid ${UI.line.strong}` : "2px solid transparent", borderRadius: 6, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", position: "relative", width: "100%", textAlign: "right" }}>
                  <span className={animTab === t.id ? "nav-icon-anim" : ""}><t.Icon active={active}/></span>
                  <span style={{ fontSize: 16, fontWeight: active ? 700 : 400, color: active ? UI.state.fg : UI.text.secondary }}>{t.label}</span>
                  {null}
                </button>
              );
            })}
          </div>
          <div style={{ flexShrink: 0, padding: "10px 12px", borderTop: `1px solid ${UI.line.hairline}` }}>
            <div dir="ltr" style={{ fontSize: 13, color: UI.text.muted, textAlign: "center", fontWeight: 600, unicodeBidi: "isolate" }}>{fxLabel}</div>
          </div>
          {/* Savanna slice — the brand mark at the foot of the sidebar. Height,
              mask and opacity come from the spec; the caption plate sits over
              it and carries meta text only, never body copy. */}
          <div className="si-savanna-slice" aria-hidden="true">
            <div className="si-savanna-caption">
              <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.45 }}>הסוריקטה עומדת זקופה<br/>וצופה למרחק.</div>
              <div style={{ fontSize: 11.5, opacity: .72, marginTop: 4 }}>מידע בלבד · אינו ייעוץ השקעות</div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      {isDesktop && <StatusRow standalone lang={lang} marketTag={tab !== "home"}/>}
      {!isDesktop && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: UI.surface.raised, borderTop: `1px solid ${UI.line.hairline}`, zIndex: 100 }}>
          {/* Status row rides on top of the nav — never covers content */}
          <div style={{ borderBottom: `1px solid ${UI.line.hairline}` }}><StatusRow lang={lang} marketTag={tab !== "home"}/></div>
          <div style={{ display: "flex", justifyContent: "space-around", padding: isMobile ? "8px 0 14px" : "12px 0 18px" }}>
          {TABS.filter(t => !t.mobileHide).map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => handleTabClick(t.id)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, position: "relative", padding: "0 8px", flex: 1, minHeight: 44 }}>
                {/* Soft gold halo behind the active tab's icon+label — always
                    mounted so the background transitions smoothly instead of
                    popping in/out; sized/positioned so icons never shift. */}
                <span style={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 56, height: 44, borderRadius: 16,
                  background: active ? UI.state.surface : "transparent",
                  transition: "background .25s ease",
                  pointerEvents: "none", zIndex: 0,
                }}/>
                <span className={animTab === t.id ? "nav-icon-anim" : ""} style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <t.Icon active={active}/>
                </span>
                <span style={{ position: "relative", zIndex: 1, fontSize: 13, fontWeight: active ? 700 : 400, color: active ? UI.state.fg : UI.text.secondary }}>{t.label}</span>
              </button>
            );
          })}
          </div>
        </div>
      )}

      {/* Mobile Academy full-screen overlay */}
      {ACADEMY_ENABLED && !isDesktop && showAcademy && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinejoin="round"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinejoin="round"/></svg>
              {lang === "en" ? "Academy" : "אקדמיה"}
            </span>
            <button onClick={() => setShowAcademy(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:3, padding:"6px 14px", fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <ChunkBoundary><Suspense fallback={<AcademyLoading/>}><AcademyTab lang={lang}/></Suspense></ChunkBoundary>
          </div>
        </div>
      )}

      {/* Mobile Calculators full-screen overlay */}
      {CALCULATORS_ENABLED && !isDesktop && showCalc && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="4" y="2" width="16" height="20" rx="2" stroke={UI.text.secondary} strokeWidth="1.5"/><rect x="7" y="5" width="10" height="4" rx="1" stroke={UI.text.secondary} strokeWidth="1.5"/><circle cx="8" cy="13" r="1.2" fill={UI.text.secondary}/><circle cx="12" cy="13" r="1.2" fill={UI.text.secondary}/><circle cx="16" cy="13" r="1.2" fill={UI.text.secondary}/><circle cx="8" cy="17" r="1.2" fill={UI.text.secondary} opacity=".6"/><circle cx="12" cy="17" r="1.2" fill={UI.text.secondary} opacity=".6"/><circle cx="16" cy="17" r="1.2" fill={UI.text.secondary} opacity=".6"/></svg>
              {lang === "en" ? "Calculators" : "מחשבונים"}
            </span>
            <button onClick={() => setShowCalc(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:3, padding:"6px 14px", fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <CalculatorsTab lang={lang}/>
          </div>
        </div>
      )}

      {/* Mobile Q&A full-screen overlay */}
      {!isDesktop && showQA && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke={UI.text.secondary} strokeWidth="1.5"/>
                <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="17" r="0.6" fill={UI.text.secondary}/>
              </svg>
              {lang === "en" ? "Q&A" : "שאלות נפוצות"}
            </span>
            <button onClick={() => setShowQA(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:3, padding:"6px 14px", fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <QATab lang={lang}/>
          </div>
        </div>
      )}

      {/* Mobile Settings full-screen overlay */}
      {!isDesktop && showSettings && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.sunken, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <Icon name="settings" size={17} color={UI.text.secondary}/>{lang === "en" ? "Settings" : "הגדרות"}
            </span>
            <button onClick={() => setShowSettings(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:8, padding:"8px 16px", minHeight:44, fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <SettingsTab isPro={isPro} onUnlockPro={() => setIsPro(true)} cur={cur} setCur={setCur} lang={lang} toggleLang={toggleLang} t={t} portfolio={portfolio} onResetProfile={resetProfile} session={session}/>
          </div>
        </div>
      )}

      {/* Mobile Simulator full-screen overlay */}
      {!isDesktop && showSimulator && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.sunken, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              {lang === "en" ? "Practice account" : "חשבון תרגול"}
            </span>
            <button onClick={() => setShowSimulator(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:8, padding:"8px 16px", minHeight:44, fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <SimAccountTab session={session} lang={lang}/>
          </div>
        </div>
      )}

      {/* Mobile Exposure full-screen overlay */}
      {!isDesktop && showExposure && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <Icon name="globe" size={17} color={UI.text.secondary}/>{lang === "en" ? "Portfolio exposures" : "חשיפות התיק"}
            </span>
            <button onClick={() => setShowExposure(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:8, padding:"8px 16px", minHeight:44, fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <ExposureTab portfolio={portfolio} cur={cur}/>
          </div>
        </div>
      )}

      {/* Mobile "מה אם" full-screen overlay (sprint 13, stage 3) */}
      {!isDesktop && showScenario && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <Icon name="scale" size={17} color={UI.text.secondary}/>{lang === "en" ? "What if" : "מה אם"}
            </span>
            <button onClick={() => setShowScenario(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:8, padding:"8px 16px", minHeight:44, fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <ScenarioTab portfolio={portfolio} portfolioReady={portfolioReady}/>
          </div>
        </div>
      )}

      {/* Mobile Profile full-screen overlay */}
      {INVESTOR_PROFILE_ENABLED && !isDesktop && showProfile && (
        <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position:"fixed", inset:0, zIndex:210, background:UI.surface.page, overflowY:"auto" }}>
          <div style={{ position:"sticky", top:0, background:UI.surface.raised, borderBottom:`1px solid ${UI.line.hairline}`, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", zIndex:1 }}>
            <span style={{ fontWeight:800, fontSize:15, color:UI.text.primary, display:"flex", alignItems:"center", gap:8 }}>
              <Icon name="person" size={17} color={UI.text.secondary}/>{lang === "en" ? "My Profile" : "הפרופיל שלי"}
            </span>
            <button onClick={() => setShowProfile(false)}
              style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:3, padding:"6px 14px", fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:"pointer" }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="x" size={13}/>{lang === "en" ? "Close" : "סגור"}</span>
            </button>
          </div>
          <div style={{ padding:"14px 16px 90px" }}>
            <MyProfileTab lang={lang} onResetProfile={() => { setShowProfile(false); resetProfile(); }}/>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Drawer */}
      {!isDesktop && (
        <MobileSidebar
          open={showSidebar}
          onClose={() => setShowSidebar(false)}
          tab={tab}
          onTabClick={handleTabClick}
          lang={lang}
          cur={cur}
          onCurrencyChange={setCur}
          onSignOut={onSignOut}
          isAdmin={isAdmin}
          onLang={(code) => { if (lang !== code) toggleLang(); }}
        />
      )}

      {/* One-time acknowledgement (1.9) · shown only once the column exists */}
      <DisclaimerConsent userId={session?.user?.id} isGuest={isGuest}/>

      {/* Name only (first launch, while the questionnaire is hidden) */}
      {!INVESTOR_PROFILE_ENABLED && showNameOnly && (
        <NameOnlyFlow onDone={() => setShowNameOnly(false)}/>
      )}

      {/* Onboarding (first-launch only) */}
      {INVESTOR_PROFILE_ENABLED && showOnboarding && (
        <OnboardingFlow onComplete={() => {
          // Apply the freshly chosen display style immediately (OnboardingFlow
          // mirrors it to localStorage on save); the DB is the source of truth
          // on the next load.
          try {
            const saved = JSON.parse(localStorage.getItem(PROFILE_KEY) || "{}");
            if (saved?.display_style) setDisplayStyle(saved.display_style);
          } catch { /* ignore */ }
          setShowOnboarding(false);
        }}/>
      )}

      {/* Global stock page (opened from any ticker via si-open-stock) */}
      {stockPage && (
        <StockPage
          // A new company is a new page: its placeholders start their time
          // limit again (sprint 14, 3.2, review).
          key={stockPage.symbol}
          symbol={stockPage.symbol}
          name={stockPage.name}
          price={stockPage.price}
          portfolio={portfolio}
          watchlist={watchlist}
          cur={cur}
          lang={lang}
          userId={session?.user?.id}
          isGuest={isGuest}
          watch={watch}
          onClose={() => setStockPage(null)}
        />
      )}

      {/* Alerts Drawer (global bottom-sheet) */}
      {PRICE_ALERTS_ENABLED && alertsFor !== null && (
        <AlertsDrawer
          forSymbol={alertsFor}
          alerts={alerts}
          triggered={triggered}
          portfolio={portfolio}
          watchlist={watchlist}
          cur={cur}
          onAdd={addAlertHandler}
          onRemove={removeAlertHandler}
          onClose={() => setAlertsFor(null)}
        />
      )}

      {/* Admin Dashboard overlay */}
      {showAdmin && isAdmin && (
        <div dir="rtl" style={{ position: "fixed", inset: 0, zIndex: 210, background: UI.surface.page, overflowY: "auto" }}>
          <AdminDashboard currentUserId={session.user.id} onClose={() => setShowAdmin(false)} />
        </div>
      )}

      {/* Privacy Footer */}
      <footer style={{ padding: isMobile ? "28px 16px 120px" : "36px 40px 56px", marginRight: isDesktop ? 220 : 0, borderTop: `1px solid ${UI.line.hairline}`, marginTop: 24 }}>
        <div style={{ display:"flex", flexDirection:"column", gap:6, textAlign:"center" }}>
          <div dir="ltr" style={{ fontWeight:700, fontSize:13, color:UI.accent.base, letterSpacing:".5px", fontFamily:FONT.latin, unicodeBidi:"isolate" }}>Suricata</div>
          <div style={{ fontSize:10, color:UI.text.muted, lineHeight:1.7, maxWidth:560, margin:"0 auto" }}>{t.footerDisclaimer}</div>
          <div style={{ fontSize:10, color:UI.text.muted }}>
            <span style={{ color:UI.line.hairline, margin:"0 6px" }}>|</span>
            {t.footerAccuracy}
            <span style={{ color:UI.line.hairline, margin:"0 6px" }}>|</span>
            {t.footerPrivacy}
            <span style={{ color:UI.line.hairline, margin:"0 6px" }}>|</span>
            {/* The footer already says where the data is kept; the policy is
                what that sentence is short for. Existing copy untouched. */}
            <button
              type="button"
              onClick={openPrivacy}
              style={{
                background:"none", border:"none", padding:0, cursor:"pointer",
                color:UI.text.secondary, fontSize:10, fontFamily:"inherit",
                textDecoration:"underline", textUnderlineOffset:2,
              }}>
              {t.privacyLink}
            </button>
            <span style={{ color:UI.line.hairline, margin:"0 6px" }}>|</span>
          </div>
          <div style={{ fontSize:9, color:UI.text.muted, marginTop:2 }}>{t.footerCopyright}</div>
        </div>
      </footer>
    </div>
  );
}
